import { useMemo, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Sparkles, X } from "../../components/icons";
import type { DocumentTemplate, DocumentType } from "../../lib/api";
import {
  fetchTemplateFile,
  suggestFormPages,
  suggestFormSchema,
  toWrite,
  useSaveDocumentType,
  type FormFieldHint,
  type ProposedFormField,
  type ProposedFormPage,
} from "../../lib/documentTypes";
import { applyAddressGroups, findAddressGroups, guessParent } from "./addressFields";
import {
  AiPagesProposal,
  PageEditor,
  StepsSummary,
  newPageId,
  regroup,
  type FormPage,
} from "./formPages";
import { scanBlanks } from "../../lib/templateBlanks";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * แก้ "ຟອມກອກ" ของชนิดเอกสาร = `form_schema` (ADR-0021) — super admin เท่านั้น
 *
 * ตัวนี้คือที่เดียวที่บอกว่าแอปมีช่องกรอกอะไร: wizard ของ A01 วาดช่องตาม x-step/x-order/
 * x-widget ฯลฯ ที่บันทึกจากจอนี้ แอปเห็นทันทีไม่ต้องออกเวอร์ชันใหม่ และ server เช็คช่องที่ติ๊ก
 * "ບັງຄັບ" ตอนยื่น · หน้าวางช่อง PDF เลือกชื่อช่องจากรายการเดียวกันนี้
 *
 * ช่องระบบ (x-system: for_self/residency) ไม่แสดงและถูกเก็บไว้ตามเดิม · key อื่นของ property
 * ที่จอนี้ไม่รู้จักก็ถูกเก็บไว้ ไม่หายเพราะเปิดมาแก้
 */

/** ชนิดเอกสารที่มี wizard ในแอป (ต้องตรงกับ WIZARD_CODES ใน web-mobile) — ที่เหลือใช้ฟอร์มทั่วไป */
const WIZARD_CODES = new Set(["A01", "A02"]);
/**
 * เอกสารที่จัดช่องด้วย "ขั้นตายตัว" (x-step: ຂໍ້ມູນຜູ້ຂໍ / ທີ່ຢູ່ / ສຳມະໂນຄົວ) — A01 เท่านั้น
 * wizard อื่น (A02, แม่แบบ AI ที่ตั้ง x-wizard) จัดด้วย "ขั้นตอนการกรอก" ที่ผู้ดูแลกำหนดเอง (x-pages)
 */
const STEP_CODES = new Set(["A01"]);

/** ตรงกับ usesWizard ของ web-mobile: รหัสข้างบน หรือ form_schema ที่ตั้ง `x-wizard: true` */
function usesWizard(doc: DocumentType): boolean {
  return WIZARD_CODES.has(doc.code) || (doc.form_schema as Record<string, unknown> | null)?.["x-wizard"] === true;
}

const STEPS = [
  { value: "applicant", label: "ຂໍ້ມູນຜູ້ຂໍ" },
  { value: "place", label: "ທີ່ຢູ່ປັດຈຸບັນ & ຈຸດປະສົງ" },
  { value: "household", label: "ສຳມະໂນຄົວ & ພໍ່ແມ່" },
] as const;
type Step = (typeof STEPS)[number]["value"];

const WIDGETS = [
  { value: "text", label: "ຂໍ້ຄວາມ" },
  { value: "textarea", label: "ຂໍ້ຄວາມຫຼາຍແຖວ" },
  { value: "number", label: "ຕົວເລກ" },
  { value: "date", label: "ວັນທີ" },
  { value: "phone", label: "ເບີໂທ (+856)" },
  { value: "select", label: "ເລືອກຈາກລາຍການ" },
  { value: "province-name", label: "ເລືອກແຂວງ" },
  { value: "district-name", label: "ເລືອກເມືອງ" },
  { value: "village-name", label: "ເລືອກບ້ານ" },
  // ไม่ต้องมีช่องแขวง/เมืองคู่ — พิมพ์ค้นชื่อบ้านทั่วประเทศแล้วเลือก (เก็บชื่อบ้าน)
  { value: "village-search", label: "ຄົ້ນຫາບ້ານ (ທົ່ວປະເທດ)" },
] as const;
/** ช่องทั่วไปใช้ได้แค่ข้อความ — ฟอร์มทั่วไปของแอปยังวาดชนิดอื่นไม่เป็น */
const GENERIC_WIDGETS = new Set(["text", "textarea"]);

/**
 * ชุดที่อยู่ที่ตรึงเขตของคำร้อง (province_id/district_id/village_id) — server ใช้กำหนดว่าคำร้อง
 * ไปถึงตาแสงไหน จึงเปลี่ยนชนิด/ลบไม่ได้ แก้ได้แค่ป้าย บังคับ และขั้น
 */
const ADDRESS_WIDGETS: Record<string, string> = {
  "address-province": "ແຂວງ (ທີ່ຢູ່ປັດຈຸບັນ — ກຳນົດຕາແສງທີ່ຮັບຄຳຮ້ອງ)",
  "address-district": "ເມືອງ (ທີ່ຢູ່ປັດຈຸບັນ — ກຳນົດຕາແສງທີ່ຮັບຄຳຮ້ອງ)",
  "address-village": "ບ້ານ (ທີ່ຢູ່ປັດຈຸບັນ — ກຳນົດຕາແສງທີ່ຮັບຄຳຮ້ອງ)",
};

const PREFILLS = [
  { value: "", label: "— ບໍ່ເຕີມ —" },
  // ຊື່ ແລະ ນາມສະກຸນ = ชื่อเต็ม แต่ถ้ามีช่องอื่นตั้งเป็น "ນາມສະກຸນ" ช่องนี้จะได้แค่ชื่อ
  { value: "fullName", label: "ຊື່ ແລະ ນາມສະກຸນ (ຫຼື ຊື່ ຖ້າມີຊ່ອງນາມສະກຸນແຍກ)" },
  { value: "lastName", label: "ນາມສະກຸນ (ແຍກຈາກຊ່ອງຊື່)" },
  { value: "idNumber", label: "ເລກບັດປະຈຳຕົວ" },
  { value: "dateOfBirth", label: "ວັນເດືອນປີເກີດ" },
];

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
/** key ของ property ที่จอนี้จัดการเอง — ที่เหลือใน property เดิมถูกเก็บไว้ตามเดิม */
const MANAGED = [
  "type", "title", "enum", "default", "x-step", "x-order", "x-widget", "x-section", "x-parent", "x-prefill", "x-remember",
  "x-page",
];

interface Field {
  key: string;
  label: string;
  widget: string;
  required: boolean;
  step: Step;
  section: string;
  parent: string;
  options: string;
  defaultValue: string;
  prefill: string;
  /** แอปจำค่าจากคำร้องครั้งก่อนมาเติมให้ (ค่าเริ่มต้น) — ปิดสำหรับช่องที่เปลี่ยนทุกครั้ง เช่น ลงวันที่ */
  remember: boolean;
  /** ขั้นตอนการกรอก (x-page) — โหมดขั้นตอนเท่านั้น, "" = ยังไม่ได้แบ่ง */
  page: string;
  /** x-step เดิมตามที่อยู่ใน schema ("" = ไม่ระบุ) — โหมดขั้นตอนเขียนกลับตามเดิม ไม่ตั้งเอง */
  rawStep: string;
  /** property เดิมจาก schema — เก็บ key ที่จอไม่รู้จักไว้ตอนบันทึก */
  raw: Record<string, unknown>;
  isNew: boolean;
}

type Schema = { properties?: Record<string, Record<string, unknown>>; required?: unknown } & Record<string, unknown>;

const str = (v: unknown) => (typeof v === "string" ? v : "");

function parse(doc: DocumentType): {
  fields: Field[];
  system: Record<string, Record<string, unknown>>;
  pages: FormPage[];
} {
  const schema = (doc.form_schema ?? {}) as Schema;
  const props = schema.properties ?? {};
  const required = new Set(Array.isArray(schema.required) ? (schema.required as unknown[]).map(str) : []);
  const system: Record<string, Record<string, unknown>> = {};
  const fields: Field[] = [];
  Object.entries(props).forEach(([key, p]) => {
    if (p["x-system"] === true) {
      system[key] = p;
      return;
    }
    const step = str(p["x-step"]);
    fields.push({
      key,
      label: str(p.title) || key,
      widget: str(p["x-widget"]) || "text",
      required: required.has(key),
      step: (STEPS.some((s) => s.value === step) ? step : "household") as Step,
      section: str(p["x-section"]),
      parent: str(p["x-parent"]),
      options: Array.isArray(p.enum) ? (p.enum as unknown[]).map(str).join("\n") : "",
      defaultValue: str(p.default),
      prefill: str(p["x-prefill"]),
      remember: p["x-remember"] !== false,
      page: str(p["x-page"]),
      rawStep: step,
      raw: p,
      isNew: false,
    });
  });
  const order = (f: Field, i: number) => (typeof f.raw["x-order"] === "number" ? (f.raw["x-order"] as number) : 10_000 + i);
  const indexed = fields.map((f, i) => ({ f, o: order(f, i) }));
  const stepIndex = (s: Step) => STEPS.findIndex((x) => x.value === s);
  indexed.sort((a, b) => stepIndex(a.f.step) - stepIndex(b.f.step) || a.o - b.o);
  return { fields: indexed.map((x) => x.f), system, pages: parsePages(schema["x-pages"]) };
}

/** `x-pages` ของ schema — รายการที่ไม่มี id/ชื่อ หรือ id ซ้ำ ถูกข้าม (อ่านแบบเดียวกับ formPagesOf ของแอป) */
function parsePages(raw: unknown): FormPage[] {
  if (!Array.isArray(raw)) return [];
  const out: FormPage[] = [];
  for (const item of raw as unknown[]) {
    if (!item || typeof item !== "object") continue;
    const p = item as Record<string, unknown>;
    const id = str(p.id).trim();
    if (!id || out.some((x) => x.id === id)) continue;
    const scan = str(p.scan);
    out.push({
      id,
      title: str(p.title),
      lead: str(p.lead),
      scan: ["id_card", "family_book", "passport", "document"].includes(scan) ? (scan as FormPage["scan"]) : "",
    });
  }
  return out;
}

function build(
  doc: DocumentType,
  fields: Field[],
  system: Record<string, Record<string, unknown>>,
  mode: "steps" | "pages" | "plain",
  pages: FormPage[],
): Record<string, unknown> {
  const schema = (doc.form_schema ?? {}) as Schema;
  const properties: Record<string, Record<string, unknown>> = { ...system };
  const counter: Record<string, number> = {};
  for (const f of fields) {
    const kept = Object.fromEntries(Object.entries(f.raw).filter(([k]) => !MANAGED.includes(k)));
    const p: Record<string, unknown> = { ...kept, type: "string", title: f.label.trim() };
    if (mode === "steps") {
      counter[f.step] = (counter[f.step] ?? 0) + 1;
      p["x-step"] = f.step;
      p["x-order"] = (counter[f.step] ?? 1) * 10;
    } else if (mode === "pages") {
      // x-step เดิมคงไว้ (ช่องขั้นผู้ขอที่แม่แบบ AI ตั้ง) — ลำดับบนจอ = ลำดับในแอป
      if (f.rawStep) p["x-step"] = f.rawStep;
      counter[f.rawStep] = (counter[f.rawStep] ?? 0) + 1;
      p["x-order"] = (counter[f.rawStep] ?? 1) * 10;
      if (pages.length > 0 && f.rawStep !== "applicant" && f.page) p["x-page"] = f.page;
    }
    if (f.widget !== "text") p["x-widget"] = f.widget;
    if (f.section.trim()) p["x-section"] = f.section.trim();
    if ((f.widget === "district-name" || f.widget === "village-name") && f.parent) p["x-parent"] = f.parent;
    if (f.prefill) p["x-prefill"] = f.prefill;
    if (!f.remember) p["x-remember"] = false;
    if (f.widget === "select") p.enum = optionList(f.options);
    if (f.defaultValue.trim()) p.default = f.defaultValue.trim();
    properties[f.key] = p;
  }
  const out: Record<string, unknown> = {
    ...schema,
    type: "object",
    properties,
    required: fields.filter((f) => f.required).map((f) => f.key),
  };
  if (mode === "pages" && pages.length > 0) {
    out["x-pages"] = pages.map((pg) => ({
      id: pg.id,
      title: pg.title.trim(),
      ...(pg.lead.trim() ? { lead: pg.lead.trim() } : {}),
      ...(pg.scan ? { scan: pg.scan } : {}),
    }));
  } else if (mode === "pages") {
    delete out["x-pages"];
  }
  return out;
}

const optionList = (raw: string) =>
  raw
    .split("\n")
    .map((o) => o.trim())
    .filter(Boolean);

/** ช่องแม่ที่ถูกชนิด: เมืองต้องอยู่ใต้แขวง บ้านต้องอยู่ใต้เมือง */
const PARENT_WIDGET: Record<string, string> = { "district-name": "province-name", "village-name": "district-name" };

function problems(fields: Field[], reserved: Set<string>, wizard: boolean, pages: FormPage[] = []): string[] {
  const out: string[] = [];
  pages.forEach((pg, i) => {
    if (!pg.title.trim()) out.push(`ຂັ້ນຕອນທີ ${String(i + 1)}: ກະລຸນາໃສ່ຊື່ຂັ້ນຕອນ`);
  });
  const seen = new Set<string>();
  for (const f of fields) {
    const name = f.label.trim() || f.key;
    if (!KEY_PATTERN.test(f.key)) out.push(`${name}: ລະຫັດຊ່ອງໃຊ້ໄດ້ສະເພາະ a-z, 0-9 ແລະ _ ແລະ ຂຶ້ນຕົ້ນດ້ວຍຕົວອັກສອນ`);
    if (seen.has(f.key)) out.push(`${name}: ລະຫັດຊ່ອງ ${f.key} ຊ້ຳກັນ`);
    seen.add(f.key);
    if (f.isNew && reserved.has(f.key)) out.push(`${name}: ລະຫັດ ${f.key} ລະບົບໃຊ້ຢູ່ແລ້ວ — ເລືອກລະຫັດອື່ນ`);
    if (!f.label.trim()) out.push(`${f.key}: ກະລຸນາໃສ່ຊື່ຊ່ອງ`);
    if (f.widget === "select" && optionList(f.options).length === 0) out.push(`${name}: ຊ່ອງແບບເລືອກຕ້ອງມີຕົວເລືອກຢ່າງໜ້ອຍ 1 ລາຍການ`);
    const parentWidget = PARENT_WIDGET[f.widget];
    if (parentWidget) {
      const parent = fields.find((x) => x.key === f.parent);
      if (!parent || parent.widget !== parentWidget)
        out.push(
          `${name}: ຕ້ອງເລືອກຊ່ອງແມ່ (${parentWidget === "province-name" ? "ເລືອກແຂວງ" : "ເລືອກເມືອງ"}) — ຫຼື ກົດ “ແຂວງ/ເມືອງ/ບ້ານ → ລາຍການເລືອກ” ໃຫ້ລະບົບຈັດໃຫ້`,
        );
      else if (wizard && parent.step !== f.step) out.push(`${name}: ຕ້ອງຢູ່ຂັ້ນດຽວກັບຊ່ອງແມ່`);
    }
  }
  return out;
}

export function FormSchemaEditor({
  doc,
  template,
  reservedKeys,
  canEdit,
  onClose,
}: {
  doc: DocumentType;
  template: DocumentTemplate | undefined;
  /** key ที่ระบบเติมเอง (ช่องมาตรฐานของแบบฟอร์ม PDF) — ช่องใหม่ห้ามใช้ซ้ำ */
  reservedKeys: string[];
  canEdit: boolean;
  onClose: () => void;
}) {
  const wizard = usesWizard(doc);
  // steps = ขั้นตายตัวของ A01 · pages = ขั้นตอนการกรอกที่ผู้ดูแลกำหนด (wizard อื่น) · plain = ฟอร์มทั่วไปของแอป
  const mode: "steps" | "pages" | "plain" = STEP_CODES.has(doc.code) ? "steps" : wizard ? "pages" : "plain";
  const stepMode = mode === "steps";
  const pageMode = mode === "pages";
  const initial = useMemo(() => parse(doc), [doc]);
  const [pages, setPages] = useState<FormPage[]>(initial.pages);
  const [fields, setFields] = useState<Field[]>(() =>
    mode === "pages" ? regroup(initial.fields, initial.pages) : initial.fields,
  );
  const [selected, setSelected] = useState<string | null>(initial.fields[0]?.key ?? null);
  // ขั้นตอนที่เลือกแก้ (คลิกหัวขั้นในรายการ/แถบขั้นตอน) — เลือกช่องแล้วล้าง
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  // ข้อเสนอของ AI จัดขั้นตอน (ยังไม่ใช้จนผู้ดูแลกด) + คำแนะนำรายช่องที่ติ๊กไว้
  const [pageProposal, setPageProposal] = useState<{ pages: ProposedFormPage[]; hints: FormFieldHint[] } | null>(null);
  const [pickedHints, setPickedHints] = useState<Set<string>>(new Set());
  const [pagesBusy, setPagesBusy] = useState(false);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveDocumentType();
  const reserved = useMemo(
    () => new Set(reservedKeys.filter((k) => !initial.fields.some((f) => f.key === k))),
    [reservedKeys, initial.fields],
  );
  const placedOnPdf = useMemo(() => new Set((template?.fields ?? []).map((f) => f.name)), [template]);

  const current = fields.find((f) => f.key === selected) ?? null;
  const index = current ? fields.indexOf(current) : -1;
  const locked = current ? current.widget in ADDRESS_WIDGETS : false;

  function patch(p: Partial<Field>) {
    if (!current) return;
    setFields((all) => {
      const next = all.map((f) => (f === current ? { ...f, ...p } : f));
      // ย้ายขั้นตอนจากช่อง "ຢູ່ຂັ້ນຕອນ" = จัดลำดับใหม่ให้ตรงกับที่แอปเห็น
      return p.page !== undefined && pageMode ? regroup(next, pages) : next;
    });
    if (p.key !== undefined) setSelected(p.key);
  }

  function selectField(key: string) {
    setSelected(key);
    setSelectedPage(null);
  }
  function selectPage(id: string) {
    setSelectedPage(id);
    setSelected(null);
    setPageProposal(null);
  }

  /* ---------- ขั้นตอนการกรอก (x-pages) ---------- */
  function updatePages(next: FormPage[]) {
    setPages(next);
    setFields((all) => regroup(all, next));
  }
  function addPage() {
    const id = newPageId();
    const page: FormPage = { id, title: `ຂັ້ນຕອນ ${String(pages.length + 1)}`, lead: "", scan: "" };
    if (pages.length === 0) {
      // แบ่งครั้งแรก: ทุกช่องเดิมอยู่ขั้นแรกก่อน แล้วค่อยย้ายออก
      setPages([page]);
      setFields((all) => regroup(all.map((f) => (f.rawStep === "applicant" ? f : { ...f, page: id })), [page]));
    } else {
      updatePages([...pages, page]);
    }
    selectPage(id);
  }
  function patchPage(id: string, p: Partial<FormPage>) {
    setPages((all) => all.map((pg) => (pg.id === id ? { ...pg, ...p } : pg)));
  }
  function movePage(id: string, delta: number) {
    const i = pages.findIndex((pg) => pg.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= pages.length) return;
    const next = [...pages];
    [next[i], next[j]] = [next[j] as FormPage, next[i] as FormPage];
    updatePages(next);
  }
  function removePage(id: string) {
    const i = pages.findIndex((pg) => pg.id === id);
    if (i < 0) return;
    const target = pages[i - 1] ?? pages[i + 1];
    if (!window.confirm(`ລຶບຂັ້ນຕອນ “${pages[i]?.title ?? ""}”? ຊ່ອງໃນຂັ້ນນີ້ຈະຍ້າຍໄປ${target ? ` “${target.title}”` : "ໜ້າດຽວ"}`)) return;
    const next = pages.filter((pg) => pg.id !== id);
    setPages(next);
    setFields((all) => regroup(all.map((f) => (f.page === id ? { ...f, page: target?.id ?? "" } : f)), next));
    setSelectedPage(target?.id ?? null);
  }

  function move(delta: number) {
    if (!current) return;
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const neighbour = fields[target] as Field;
    // ขั้นผู้ขอเป็นขั้นคงที่ของแอป — ย้ายช่องข้ามเข้า/ออกไม่ได้ (เปลี่ยนที่จอแม่แบบ AI)
    if (pageMode && (neighbour.rawStep === "applicant") !== (current.rawStep === "applicant")) return;
    // ย้ายข้ามขั้นตอน = ไปอยู่ขั้นของช่องที่ชนแทนที่จะสลับ (ช่องสุดท้ายของขั้นเลื่อนลง = ช่องแรกของขั้นถัดไป)
    if (pageMode && neighbour.page !== current.page) {
      patch({ page: neighbour.page });
      return;
    }
    const next = [...fields];
    [next[index], next[target]] = [next[target] as Field, current];
    // ย้ายข้ามขั้น = ย้ายไปอยู่ขั้นของช่องที่สลับด้วย ลำดับบนจอจะได้ตรงกับที่แอปเห็น
    if (stepMode && neighbour.step !== current.step) next[target] = { ...current, step: neighbour.step };
    setFields(next);
  }

  function add() {
    let n = fields.length + 1;
    while (fields.some((f) => f.key === `field_${String(n)}`)) n++;
    const key = `field_${String(n)}`;
    const step = current?.step ?? "household";
    const field: Field = {
      key,
      label: "",
      widget: "text",
      required: false,
      step,
      section: "",
      parent: "",
      options: "",
      defaultValue: "",
      prefill: "",
      remember: true,
      page: (current?.rawStep === "applicant" ? "" : current?.page) || selectedPage || pages[pages.length - 1]?.id || "",
      rawStep: "",
      raw: {},
      isNew: true,
    };
    if (pageMode) {
      // ช่องใหม่ต่อท้ายขั้นตอนของช่อง/ขั้นที่เลือกอยู่ (regroup จัดตำแหน่งให้)
      const at = fields.indexOf(current as Field);
      const next = [...fields];
      next.splice(at >= 0 && current?.rawStep !== "applicant" ? at + 1 : next.length, 0, field);
      setFields(regroup(next, pages));
      selectField(key);
      return;
    }
    // ช่องใหม่ต่อท้ายขั้นเดียวกับช่องที่เลือกอยู่
    const lastInStep = fields.map((f) => f.step).lastIndexOf(step);
    const next = [...fields];
    next.splice(lastInStep + 1, 0, field);
    setFields(next);
    setSelected(key);
  }

  /* ---------- ให้ AI เสนอช่องที่ยังขาด (จากแบบฟอร์ม PDF) ---------- */
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [proposals, setProposals] = useState<ProposedFormField[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [aiNote, setAiNote] = useState<string | null>(null);

  async function runAiSuggest() {
    setAiBusy(true);
    setAiError(null);
    setAiNote(null);
    try {
      // ข้อความ + จุดว่างอ่านจาก text layer ของไฟล์เอง (pdf.js) — ไฟล์สแกนได้ข้อความว่าง ซึ่ง server
      // ยังเสนอชื่อช่องที่วางบน PDF แล้วแต่ไม่มีช่องกรอกให้ได้
      const file = await fetchTemplateFile(doc.id);
      const pdf = await pdfjs.getDocument({ data: new Uint8Array(file) }).promise;
      const scan = await scanBlanks(pdf);
      const got = await suggestFormSchema(doc.id, {
        text: scan.text,
        existing: fields.map((f) => ({ key: f.key, label: f.label || f.key })),
      });
      setProposals(got);
      setPicked(new Set(got.map((p) => p.key)));
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  }

  function addProposals() {
    if (!proposals) return;
    const chosen = proposals.filter((p) => picked.has(p.key) && !fields.some((f) => f.key === p.key));
    if (chosen.length === 0) {
      setProposals(null);
      return;
    }
    const step = current?.step ?? "household";
    const added: Field[] = chosen.map((p) => ({
      key: p.key,
      label: p.label_lo,
      widget: p.widget,
      required: p.required,
      step,
      section: "",
      parent: "",
      options: "",
      defaultValue: "",
      prefill: "",
      // วันที่/เหตุผลเปลี่ยนทุกครั้ง ไม่ควรจำไปเติมคำร้องครั้งหน้า
      remember: p.widget !== "date" && p.widget !== "textarea",
      page: (current?.rawStep === "applicant" ? "" : current?.page) || pages[pages.length - 1]?.id || "",
      rawStep: "",
      raw: {},
      isNew: true,
    }));
    const lastInStep = fields.map((f) => f.step).lastIndexOf(step);
    const next = [...fields];
    next.splice(lastInStep + 1, 0, ...added);
    setFields(pageMode ? regroup(next, pages) : next);
    setSelected(added[0]?.key ?? selected);
    setProposals(null);
    setAiNote(`ເພີ່ມ ${String(added.length)} ຊ່ອງແລ້ວ — ກວດຊື່ ແລະ ຊະນິດ ແລ້ວກົດ “ບັນທຶກຟອມກອກ”`);
  }

  function remove() {
    if (!current || locked) return;
    const warn = placedOnPdf.has(current.key) ? "\nຊ່ອງນີ້ຖືກວາງໄວ້ໃນແບບຟອມ PDF — ຊ່ອງນັ້ນຈະວ່າງຈົນກວ່າຈະເອົາອອກ" : "";
    if (!window.confirm(`ລຶບຊ່ອງ “${current.label || current.key}” ອອກຈາກຟອມກອກບໍ?${warn}`)) return;
    const next = fields.filter((f) => f !== current);
    setFields(next);
    setSelected(next[Math.min(index, next.length - 1)]?.key ?? null);
  }

  function submit() {
    const found = problems(fields, reserved, stepMode, pageMode ? pages : []);
    if (found.length > 0) {
      setError(found.join("\n"));
      return;
    }
    setError(null);
    save.mutate(
      {
        id: doc.id,
        version: doc.version,
        body: { ...toWrite(doc), form_schema: build(doc, fields, initial.system, mode, pages) },
      },
      {
        onSuccess: onClose,
        onError: (err) => {
          setError(err.status === 409 ? "ຂໍ້ມູນຖືກແກ້ຈາກບ່ອນອື່ນ — ກະລຸນາປິດແລ້ວເປີດໃໝ່" : err.message);
        },
      },
    );
  }

  /* ---------- ແຂວງ/ເມືອງ/ບ້ານ แบบพิมพ์ → รายการเลือก ---------- */
  function convertAddresses() {
    const { groups, loneVillages, skipped } = findAddressGroups(fields);
    if (groups.length === 0 && loneVillages.length === 0) {
      setAiNote(
        skipped.length > 0
          ? `ພົບ ${String(skipped.length)} ຊ່ອງທີ່ເປັນທີ່ຢູ່ ແຕ່ບໍ່ຄົບຊຸດ (ຕ້ອງມີທັງ ແຂວງ ແລະ ເມືອງ) — ປ່ຽນທີລະຊ່ອງໄດ້ທີ່ “ຊະນິດຊ່ອງ”`
          : "ບໍ່ພົບຊ່ອງ ແຂວງ/ເມືອງ/ບ້ານ ທີ່ເປັນຂໍ້ຄວາມພິມເອງ",
      );
      return;
    }
    const list = groups
      .map((g) => `• ${g.name || "ທີ່ຢູ່ຫຼັກ"} — ${g.village ? "ແຂວງ → ເມືອງ → ບ້ານ" : "ແຂວງ → ເມືອງ"}`)
      .join("\n");
    const lone = loneVillages.map((k) => fields.find((f) => f.key === k)?.label || k);
    const message = [
      groups.length > 0
        ? `ປ່ຽນ ${String(groups.length)} ຊຸດເປັນລາຍການເລືອກ (ເລືອກແຂວງກ່ອນ ແລ້ວເມືອງ ແລະ ບ້ານຕາມ — ຈັດລຽງຕິດກັນ):\n${list}`
        : "",
      lone.length > 0
        ? `ປ່ຽນ ${String(lone.length)} ຊ່ອງທີ່ມີແຕ່ບ້ານ ເປັນ “ຄົ້ນຫາບ້ານ” (ພິມຊື່ແລ້ວເລືອກຈາກທົ່ວປະເທດ):\n• ${lone.slice(0, 8).join("\n• ")}${lone.length > 8 ? `\n… ແລະ ອີກ ${String(lone.length - 8)} ຊ່ອງ` : ""}`
        : "",
    ]
      .filter(Boolean)
      .join("\n\n");
    if (!window.confirm(message)) return;
    const loneSet = new Set(loneVillages);
    const next = applyAddressGroups(fields, groups).map((f) =>
      loneSet.has(f.key) ? { ...f, widget: "village-search", parent: "" } : f,
    );
    setFields(pageMode ? regroup(next, pages) : next);
    setAiNote(
      [
        groups.length > 0 ? `ປ່ຽນ ${String(groups.length)} ຊຸດເປັນລາຍການເລືອກ` : "",
        lone.length > 0 ? `${String(lone.length)} ຊ່ອງເປັນ ຄົ້ນຫາບ້ານ` : "",
        skipped.length > 0 ? `${String(skipped.length)} ຊ່ອງບໍ່ຄົບຊຸດ ຍັງເປັນຂໍ້ຄວາມ` : "",
      ]
        .filter(Boolean)
        .join(" · ") + " — ກົດ “ບັນທຶກຟອມກອກ”",
    );
  }

  /* ---------- AI จัดขั้นตอน + แนะนำชนิดช่อง ---------- */
  async function runAiPages() {
    const detail = fields.filter((f) => f.rawStep !== "applicant");
    if (detail.length === 0) return;
    setPagesBusy(true);
    setPagesError(null);
    try {
      const got = await suggestFormPages(
        doc.id,
        detail.map((f) => ({ key: f.key, label: f.label || f.key, widget: f.widget, required: f.required })),
      );
      setPageProposal(got);
      setPickedHints(new Set(got.hints.map((h) => h.key)));
      setSelected(null);
      setSelectedPage(null);
    } catch (e) {
      setPagesError(e instanceof Error ? e.message : String(e));
    } finally {
      setPagesBusy(false);
    }
  }

  function applyPageProposal() {
    if (!pageProposal) return;
    const next: FormPage[] = pageProposal.pages.map((p) => ({
      id: newPageId(),
      title: p.title_lo,
      lead: p.lead_lo,
      scan: p.scan,
    }));
    const pageOfKey = new Map<string, string>();
    pageProposal.pages.forEach((p, i) => {
      for (const k of p.keys) pageOfKey.set(k, next[i]!.id);
    });
    const hints = new Map(pageProposal.hints.filter((h) => pickedHints.has(h.key)).map((h) => [h.key, h]));
    const known = new Set<string>(WIDGETS.map((w) => w.value));
    setPages(next);
    setFields((all) =>
      regroup(
        all.map((f) => {
          const h = hints.get(f.key);
          const locked = f.widget in ADDRESS_WIDGETS;
          return {
            ...f,
            page: pageOfKey.get(f.key) ?? f.page,
            ...(h?.widget && known.has(h.widget) && !locked ? { widget: h.widget, parent: "" } : {}),
            ...(h && h.required !== null ? { required: h.required } : {}),
          };
        }),
        next,
      ),
    );
    setPageProposal(null);
    setSelectedPage(next[0]?.id ?? null);
    setAiNote(`ຈັດເປັນ ${String(next.length)} ຂັ້ນຕອນແລ້ວ — ກວດ ແລະ ແກ້ຕໍ່ໄດ້ ແລ້ວກົດ “ບັນທຶກຟອມກອກ”`);
  }

  const widgetOptions = WIDGETS.filter((w) => wizard || GENERIC_WIDGETS.has(w.value));
  const parentCandidates = current
    ? fields.filter((f) => f.widget === PARENT_WIDGET[current.widget] && f !== current)
    : [];
  const stepLabel = (s: Step) => STEPS.find((x) => x.value === s)?.label ?? s;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={`ຟອມກອກ ${doc.code}`}>
      <div className="modal modal--schema">
        <div className="modal__header">
          <div>
            <h2 className="modal__title">
              ຟອມກອກ · <span className="num">{doc.code}</span> {doc.name_lo}
            </h2>
            <p className="caption">
              ຊ່ອງທີ່ປະຊາຊົນກອກໃນແອັບ — ບັນທຶກແລ້ວແອັບເຫັນທັນທີ ແລະ ຊ່ອງທີ່ໝາຍ “ບັງຄັບ” ຈະຍື່ນບໍ່ໄດ້ຖ້າບໍ່ກອກ
            </p>
          </div>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
            <X size={16} />
          </button>
        </div>

        {pageMode ? (
          <StepsSummary pages={pages} selected={selectedPage} onSelect={selectPage} />
        ) : null}

        <div className="schema-editor">
          <div className="schema-editor__list">
            {fields.length === 0 ? <p className="caption">ຍັງບໍ່ມີຊ່ອງ</p> : null}
            {fields.map((f, i) => {
              const prev = fields[i - 1];
              const newStep = stepMode && (i === 0 || prev?.step !== f.step);
              // โหมดขั้นตอน: หัวกลุ่ม = ขั้นผู้ขอ (คงที่) / ขั้นตอนที่ผู้ดูแลกำหนด (กดแก้ได้) / หน้าเดียว
              const applicant = f.rawStep === "applicant";
              const newGroup =
                pageMode &&
                (i === 0 || (prev?.rawStep === "applicant") !== applicant || (!applicant && prev?.page !== f.page));
              const page = pages.find((pg) => pg.id === f.page);
              const pageNo = page ? pages.indexOf(page) + 3 : 0;
              return (
                <div key={f.key}>
                  {newStep ? <p className="schema-editor__step">{stepLabel(f.step)}</p> : null}
                  {newGroup ? (
                    applicant ? (
                      <p className="schema-editor__step">2 · ຂໍ້ມູນຜູ້ຂໍ (ຂັ້ນຄົງທີ່ຂອງແອັບ)</p>
                    ) : page ? (
                      <button
                        type="button"
                        className={page.id === selectedPage ? "fp-group is-selected" : "fp-group"}
                        onClick={() => selectPage(page.id)}
                      >
                        <span>
                          <span className="num">{pageNo}</span> · {page.title || "(ບໍ່ມີຊື່)"}
                        </span>
                        <span className="caption num">
                          {fields.filter((x) => x.page === page.id && x.rawStep !== "applicant").length} ຊ່ອງ
                        </span>
                      </button>
                    ) : (
                      <p className="schema-editor__step">3 · ລາຍລະອຽດຄຳຮ້ອງ (ໜ້າດຽວ — ຍັງບໍ່ໄດ້ແບ່ງຂັ້ນຕອນ)</p>
                    )
                  ) : null}
                  <button
                    type="button"
                    className={f.key === selected ? "schema-editor__item is-selected" : "schema-editor__item"}
                    onClick={() => {
                      selectField(f.key);
                    }}
                  >
                    <span className="body-sm">
                      {f.label || <span className="caption">(ບໍ່ມີຊື່)</span>}
                      {f.required ? <span className="required"> *</span> : null}
                    </span>
                    <span className="caption num">{f.key}</span>
                    {placedOnPdf.has(f.key) ? <span className="status-badge status-badge--info">PDF</span> : null}
                  </button>
                </div>
              );
            })}
            {canEdit ? (
              <div className="schema-editor__actions">
                <button type="button" className="btn-secondary btn-sm" onClick={add}>
                  + ເພີ່ມຊ່ອງ
                </button>
                {pageMode ? (
                  <button type="button" className="btn-secondary btn-sm" onClick={addPage}>
                    + ເພີ່ມຂັ້ນຕອນ
                  </button>
                ) : null}
                {wizard ? (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    title="ຊ່ອງ ແຂວງ/ເມືອງ/ບ້ານ ທີ່ພິມເອງ → ໃຫ້ເລືອກຈາກລາຍການ (ເກັບເປັນຊື່)"
                    onClick={convertAddresses}
                  >
                    ແຂວງ/ເມືອງ/ບ້ານ → ລາຍການເລືອກ
                  </button>
                ) : null}
                {pageMode ? (
                  <button
                    type="button"
                    className="btn-ai btn-sm"
                    disabled={pagesBusy || !fields.some((f) => f.rawStep !== "applicant")}
                    onClick={() => void runAiPages()}
                  >
                    {pagesBusy ? (
                      <>
                        <span className="btn-spinner" aria-hidden="true" /> AI ກຳລັງຈັດຂັ້ນຕອນ…
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} /> AI ຈັດຂັ້ນຕອນ ແລະ ແນະນຳຊ່ອງ
                      </>
                    )}
                  </button>
                ) : null}
                {/* อ่านช่องจากแบบฟอร์ม PDF เดิม — เลิกอัปโหลดแบบฟอร์มแล้ว (ใช้แม่แบบ AI) จึงมีปุ่มเฉพาะชนิดที่มีไฟล์อยู่ */}
                {template ? (
                  <button
                    type="button"
                    className="btn-ai btn-sm"
                    disabled={aiBusy}
                    onClick={() => void runAiSuggest()}
                  >
                    <Sparkles size={14} /> {aiBusy ? "AI ກຳລັງອ່ານແບບຟອມ…" : "AI ຊ່ວຍເພີ່ມຊ່ອງຈາກແບບຟອມ"}
                  </button>
                ) : null}
              </div>
            ) : null}
            {aiError ? <p className="field-error">{aiError}</p> : null}
            {pagesError ? <p className="field-error">{pagesError}</p> : null}
            {aiNote ? <p className="caption schema-editor__note">{aiNote}</p> : null}
            {proposals ? (
              <div className="ai-proposals">
                <p className="ai-proposals__head">
                  <Sparkles size={14} />
                  {proposals.length === 0
                    ? "ແບບຟອມນີ້ມີຊ່ອງກອກຄົບແລ້ວ — AI ບໍ່ພົບຊ່ອງທີ່ຂາດ"
                    : `AI ພົບ ${String(proposals.length)} ຊ່ອງທີ່ຍັງຂາດ — ເລືອກອັນທີ່ຈະເພີ່ມ`}
                </p>
                {proposals.map((p) => (
                  <label key={p.key} className="ai-proposals__item">
                    <input
                      type="checkbox"
                      checked={picked.has(p.key)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(p.key);
                        else next.delete(p.key);
                        setPicked(next);
                      }}
                    />
                    <span className="ai-proposals__text">
                      <span className="body-sm">
                        {p.label_lo}
                        {p.required ? <span className="required"> *</span> : null}
                      </span>
                      <span className="caption">
                        <span className="num">{p.key}</span> · {WIDGETS.find((w) => w.value === p.widget)?.label ?? p.widget}
                        {p.reason ? ` · “${p.reason}”` : ""}
                      </span>
                    </span>
                  </label>
                ))}
                <div className="ai-proposals__actions">
                  {proposals.length > 0 ? (
                    <button type="button" className="btn-secondary btn-sm" disabled={picked.size === 0} onClick={addProposals}>
                      ເພີ່ມ {picked.size} ຊ່ອງທີ່ເລືອກ
                    </button>
                  ) : null}
                  <button type="button" className="btn-outline btn-sm" onClick={() => setProposals(null)}>
                    ປິດ
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="schema-editor__detail">
            {pageProposal ? (
              <AiPagesProposal
                pages={pageProposal.pages}
                hints={pageProposal.hints}
                labelOf={(k) => fields.find((f) => f.key === k)?.label || k}
                widgetLabel={(w) => WIDGETS.find((x) => x.value === w)?.label ?? w}
                picked={pickedHints}
                onToggle={(key, on) => {
                  const next = new Set(pickedHints);
                  if (on) next.add(key);
                  else next.delete(key);
                  setPickedHints(next);
                }}
                onApply={applyPageProposal}
                onClose={() => setPageProposal(null)}
              />
            ) : selectedPage && pages.some((pg) => pg.id === selectedPage) ? (
              <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
                <PageEditor
                  page={pages.find((pg) => pg.id === selectedPage) as FormPage}
                  index={pages.findIndex((pg) => pg.id === selectedPage)}
                  total={pages.length}
                  fieldCount={fields.filter((f) => f.page === selectedPage && f.rawStep !== "applicant").length}
                  onChange={(p) => patchPage(selectedPage, p)}
                  onMove={(d) => movePage(selectedPage, d)}
                  onRemove={() => removePage(selectedPage)}
                />
              </fieldset>
            ) : !current ? (
              <p className="caption">
                {pageMode
                  ? "ເລືອກຊ່ອງ ຫຼື ຫົວຂັ້ນຕອນທາງຊ້າຍເພື່ອແກ້ໄຂ — ຟອມຍາວ ກົດ “AI ຈັດຂັ້ນຕອນ” ໃຫ້ແບ່ງເປັນໜ້າໆກ່ອນ"
                  : "ເລືອກຊ່ອງທາງຊ້າຍເພື່ອແກ້ໄຂ"}
              </p>
            ) : (
              <fieldset disabled={!canEdit} className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
                <div>
                  <label className="form-field-label" htmlFor="sf-label">
                    ຊື່ຊ່ອງ (ທີ່ຜູ້ໃຊ້ເຫັນ) <span className="required">*</span>
                  </label>
                  <input
                    id="sf-label"
                    className="input-field"
                    value={current.label}
                    maxLength={200}
                    onChange={(e) => {
                      patch({ label: e.target.value });
                    }}
                  />
                </div>
                <div>
                  <label className="form-field-label" htmlFor="sf-key">
                    ລະຫັດຊ່ອງ
                  </label>
                  <input
                    id="sf-key"
                    className="input-field num"
                    value={current.key}
                    disabled={!current.isNew}
                    onChange={(e) => {
                      patch({ key: e.target.value.trim().toLowerCase() });
                    }}
                  />
                  <p className="caption">
                    {current.isNew
                      ? "ໃຊ້ອ້າງອີງໃນແບບຟອມ PDF — ຕັ້ງແລ້ວປ່ຽນບໍ່ໄດ້ (a-z, 0-9, _)"
                      : "ຊ່ອງທີ່ມີຢູ່ແລ້ວປ່ຽນລະຫັດບໍ່ໄດ້ — ຄຳຮ້ອງເກົ່າ ແລະ ແບບຟອມ PDF ອ້າງອີງລະຫັດນີ້"}
                  </p>
                </div>
                <div>
                  <label className="form-field-label" htmlFor="sf-widget">
                    ຊະນິດຊ່ອງ
                  </label>
                  {locked ? (
                    <p className="body-sm">
                      {ADDRESS_WIDGETS[current.widget]}
                      <span className="caption"> — ລະບົບໃຊ້ກຳນົດວ່າຄຳຮ້ອງໄປເຖິງຕາແສງໃດ ຈຶ່ງປ່ຽນຊະນິດ ຫຼື ລຶບບໍ່ໄດ້</span>
                    </p>
                  ) : (
                    <select
                      id="sf-widget"
                      className="input-field"
                      value={current.widget}
                      onChange={(e) => {
                        // เลือกเมือง/บ้าน: ผูกช่องแม่ของคนเดียวกันให้เลย (แก้ทีหลังได้)
                        patch({ widget: e.target.value, parent: guessParent({ ...current, widget: e.target.value }, fields) });
                      }}
                    >
                      {widgetOptions.map((w) => (
                        <option key={w.value} value={w.value}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {current.widget === "select" ? (
                  <div>
                    <label className="form-field-label" htmlFor="sf-options">
                      ຕົວເລືອກ (ແຖວລະລາຍການ) <span className="required">*</span>
                    </label>
                    <textarea
                      id="sf-options"
                      className="input-field"
                      rows={4}
                      value={current.options}
                      onChange={(e) => {
                        patch({ options: e.target.value });
                      }}
                    />
                  </div>
                ) : null}
                {PARENT_WIDGET[current.widget] ? (
                  <div>
                    <label className="form-field-label" htmlFor="sf-parent">
                      ເລືອກຕາມຊ່ອງ <span className="required">*</span>
                    </label>
                    <select
                      id="sf-parent"
                      className="input-field"
                      value={current.parent}
                      onChange={(e) => {
                        patch({ parent: e.target.value });
                      }}
                    >
                      <option value="">— ເລືອກຊ່ອງແມ່ —</option>
                      {parentCandidates.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label || f.key}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
                  <input
                    type="checkbox"
                    checked={current.required}
                    onChange={(e) => {
                      patch({ required: e.target.checked });
                    }}
                  />
                  ບັງຄັບກອກ (ຍື່ນບໍ່ໄດ້ຖ້າວ່າງ)
                </label>
                <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
                  <input
                    type="checkbox"
                    checked={current.remember}
                    onChange={(e) => {
                      patch({ remember: e.target.checked });
                    }}
                  />
                  ຈື່ຄ່າໄວ້ເຕີມໃຫ້ໃນຄຳຮ້ອງຄັ້ງຕໍ່ໄປ (ປິດສຳລັບຊ່ອງທີ່ປ່ຽນທຸກຄັ້ງ ເຊັ່ນ ລົງວັນທີ)
                </label>
                {pageMode && pages.length > 0 && current.rawStep !== "applicant" ? (
                  <div>
                    <label className="form-field-label" htmlFor="sf-page">
                      ຢູ່ຂັ້ນຕອນ
                    </label>
                    <select
                      id="sf-page"
                      className="input-field"
                      value={current.page}
                      onChange={(e) => {
                        patch({ page: e.target.value });
                      }}
                    >
                      {pages.map((pg, i) => (
                        <option key={pg.id} value={pg.id}>
                          {String(i + 3)} · {pg.title || "(ບໍ່ມີຊື່)"}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {stepMode ? (
                  <div>
                    <label className="form-field-label" htmlFor="sf-step">
                      ຢູ່ຂັ້ນຕອນ
                    </label>
                    <select
                      id="sf-step"
                      className="input-field"
                      value={current.step}
                      onChange={(e) => {
                        patch({ step: e.target.value as Step });
                      }}
                    >
                      {STEPS.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {wizard ? (
                  <div>
                    <label className="form-field-label" htmlFor="sf-section">
                      ຫົວຂໍ້ຍ່ອຍກ່ອນຊ່ອງນີ້ (ບໍ່ບັງຄັບ)
                    </label>
                    <input
                      id="sf-section"
                      className="input-field"
                      value={current.section}
                      onChange={(e) => {
                        patch({ section: e.target.value });
                      }}
                    />
                  </div>
                ) : null}
                {!locked && (current.widget === "text" || current.widget === "number" || current.widget === "select") ? (
                  <div>
                    <label className="form-field-label" htmlFor="sf-default">
                      ຄ່າຕັ້ງຕົ້ນ (ບໍ່ບັງຄັບ)
                    </label>
                    <input
                      id="sf-default"
                      className="input-field"
                      value={current.defaultValue}
                      onChange={(e) => {
                        patch({ defaultValue: e.target.value });
                      }}
                    />
                  </div>
                ) : null}
                {wizard && !locked ? (
                  <div>
                    <label className="form-field-label" htmlFor="sf-prefill">
                      ເຕີມຈາກໂປຣໄຟລ໌ ເມື່ອຍື່ນໃຫ້ຕົນເອງ
                    </label>
                    <select
                      id="sf-prefill"
                      className="input-field"
                      value={current.prefill}
                      onChange={(e) => {
                        patch({ prefill: e.target.value });
                      }}
                    >
                      {PREFILLS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="row" style={{ gap: "var(--space-2)" }}>
                  <button type="button" className="btn-outline btn-sm" disabled={index <= 0} onClick={() => move(-1)}>
                    ↑ ຂຶ້ນ
                  </button>
                  <button
                    type="button"
                    className="btn-outline btn-sm"
                    disabled={index < 0 || index >= fields.length - 1}
                    onClick={() => move(1)}
                  >
                    ↓ ລົງ
                  </button>
                  <button type="button" className="link-action" disabled={locked} onClick={remove}>
                    ລຶບຊ່ອງນີ້
                  </button>
                </div>
              </fieldset>
            )}
          </div>
        </div>

        {error ? (
          <div className="alert-error" style={{ whiteSpace: "pre-line", marginTop: "var(--space-4)" }}>
            {error}
          </div>
        ) : null}

        {canEdit ? (
          <div className="modal__actions" style={{ marginTop: "var(--space-4)" }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={save.isPending}>
              ຍົກເລີກ
            </button>
            <button type="button" className="btn-primary" onClick={submit} disabled={save.isPending}>
              {save.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກຟອມກອກ"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
